/**
 * The search box over a dry-run choice's cards, shared by the single choice (radio cards) and the multiple choice
 * (checkbox cards). Above `CHOICE_SEARCH_LIMIT` options it narrows the cards to the words typed, and the options
 * `isKept` stay on screen whatever is typed, so narrowing never hides an answer already given. A `remote` (searchable)
 * choice always has the box, and the owner answers it: the text goes back with the dry run and the cards are
 * whatever it returns.
 */
import { useState, type ReactNode } from "react";
import type { ChoiceOption } from "@shared-core/domain/object-ui/contract";
import {
  CHOICE_SEARCH_LIMIT,
  optionMatchesSearch,
} from "@shared-core/domain/object-ui/widgets";
import { Input } from "../../primitives/input";

/** A searchable choice's box: its typed text, and a change to send to the owner. */
export interface RemoteSearch {
  text: string;
  /** The owner is still answering the last change. */
  isPending: boolean;
  onChange: (text: string) => void;
}

export function useChoiceSearch({
  label,
  options,
  disabled,
  isKept,
  remote,
}: {
  label: string;
  options: readonly ChoiceOption[];
  disabled: boolean;
  isKept: (option: ChoiceOption) => boolean;
  remote?: RemoteSearch;
}): { shown: readonly ChoiceOption[]; searchBox: ReactNode } {
  const [localSearch, setSearch] = useState("");
  if (!remote && options.length <= CHOICE_SEARCH_LIMIT)
    return { shown: options, searchBox: null };
  const search = remote ? remote.text : localSearch;
  const shown = remote
    ? options
    : options.filter(
        (option) => isKept(option) || optionMatchesSearch(option, search),
      );
  const searchBox = (
    <div className="mb-1.5 space-y-1">
      <Input
        type="search"
        value={search}
        disabled={disabled}
        placeholder={remote ? `Search ${label.toLowerCase()}…` : "Search…"}
        aria-label={`Search ${label.toLowerCase()}`}
        onChange={(event) =>
          remote
            ? remote.onChange(event.target.value)
            : setSearch(event.target.value)
        }
      />
      {shown.every(isKept) &&
        search.trim() !== "" &&
        !(remote?.isPending ?? false) && (
          <p role="status" className="text-xs text-muted-foreground">
            No other option matches.
          </p>
        )}
    </div>
  );
  return { shown, searchBox };
}
